import { Injectable } from '@angular/core'

import { StorageService } from '../../storage.service'
import { DataLayerService } from '../data-layer.service'
import { IFlowReservation } from '../../steps/step.model'
import {
  IActionField,
  ICheckoutTrack,
  IECommerceTrack,
  IEnhancedEcommerceTrack,
  IProductTrack,
  IPurchaseEvent,
  IPurchaseItem,
} from './ecommerce-tracking.interfaces'
import { ITracking, TrackingFlowStep } from '../tracking-wire.interfaces'
import { ResourceService } from 'app/shared/resources/resource.service'
import { LocaleService } from 'app/shared/locale-service'
import { reviveUtcDate } from 'app/shared/utils'
import { CookieService } from 'app/shared/cookie.service'
import { MarketService } from 'app/shared/market.service'

@Injectable()
export class EcommerceFlowTrackingService {
  private reservation: IFlowReservation
  private step: TrackingFlowStep
  private tracking: ITracking

  constructor(
    private dataLayerService: DataLayerService,
    private storageService: StorageService,
    private resourceService: ResourceService,
    private localeService: LocaleService,
    private cookieService: CookieService,
    private marketService: MarketService
  ) {}

  public track(step: TrackingFlowStep, reservation: IFlowReservation): void {
    let trackingStorageItem = this.storageService.getSbwTracking()
    if (trackingStorageItem) {
      this.reservation = reservation
      this.step = step
      this.tracking = <ITracking>JSON.parse(trackingStorageItem, (key: any, value: any) => reviveUtcDate(key, value))
      if (this.step === TrackingFlowStep.CONFIRMATION) {
        // Check if this booking has already been tracked
        if (this.hasConfirmationBeenTracked(reservation.code)) {
          return; // Exit early if already tracked
        }
        
        setTimeout(() => {
          let purchaseEvent = this.createNewPurchaseTrack()
          this.dataLayerService.push({ ecommerce: null })
          this.dataLayerService.push(purchaseEvent)
          
          // Mark this booking as tracked
          this.markConfirmationAsTracked(reservation.code)
        }, 2000)
      } else {
        let enhancedEcommerceTrack = this.createEnhancedEcommerceTrack()
        this.dataLayerService.push(enhancedEcommerceTrack)
      }
    }
  }

  public saveTrackingData(data: ITracking) {
    this.storageService.setSbwTracking(JSON.stringify(data))
  }

  public clearTracking(): void {
    this.storageService.removeSbwTracking()
  }

  private hasConfirmationBeenTracked(reservationCode: string): boolean {
    const tracked = this.storageService.getSbwTrackedConfirmations()
    return tracked === reservationCode
  }

  private markConfirmationAsTracked(reservationCode: string): void {
    this.storageService.setSbwTrackedConfirmations(reservationCode)
  }

  private createNewPurchaseTrack(): IPurchaseEvent {
    const host = window.location.host.toLowerCase()
    const useUnifiedGTM = host.indexOf('.dfds.com') > 0 || host.indexOf('localhost') >= 0
    const agent = localStorage.getItem('sbw_AgentGenericId') ?? undefined

    const purchaseEvent: IPurchaseEvent = {
      event: 'purchase',
      engagement: {
        entity: agent && agent.length > 0 ? 'agent' : 'user',
        category: 'booking',
        action: 'purchase',
      },
      ecommerce: {
        transaction_id: this.reservation.code,
        affiliation: 'SeaBook',
        amended_value: 0,
        original_value: this.tracking.price,
        total_value: this.tracking.price,
        currency: this.reservation.currencyCode,
        payment_method: this.tracking.paymentMethod,
        shipping: this.tracking.creditCardFee.toFixed(2),
        tax: '0',
        coupon: this.reservation.offerCode,
        deposit: !!this.tracking.depositAmount,
        items: this.getPruchaseItems(),
      },
      environment: {
        business_area: 'passenger',
        booking_engine: useUnifiedGTM ? 'seabookFlow' : 'seabook',
        country_code: this.localeService.getUnifiedCountryCode(),
        language_code: this.localeService.getUnifiedLanguageCode(),
        locale: this.localeService.getLocale(),
      },
      user: {
        id: sessionStorage.getItem('pax_Sub'),
        agent_id: agent,
      },
      properties: {
        passengers: {
          number_adults: this.reservation.adults,
          number_children: this.reservation.children,
          number_infants: this.reservation.infants,
          pets_selected: this.reservation.pets > 0,
          number_total: (this.reservation.adults + this.reservation.children + this.reservation.infants),
          number_outbound: this.reservation.payingPassengers,
          number_return: this.reservation.payingPassengers,
        },
        vehicles: this.reservation?.vehicleAndTrailerTypes?.length > 0
        ? this.reservation?.vehicleAndTrailerTypes.map((v) => {
            return {
                included: true,
                type: v.vehicleType,
                number: v.count,
                id: v.vehicleType,
                trailer_code: v.trailerType ?? undefined,
                pets_allowed: undefined, //We do not have a way to define if pets are allowed
            }
          })
        : [
            {
              included: false,
              type: '',
              number: 0,
              id: '',
              trailer_code: '',
              pets_allowed: undefined,
            },
          ],
        trip: {
          type: this.reservation.returnDepartureId ? 'return' : 'oneway',
          outbound_departure_date: this.reservation.outboundDepartureDate && this.reservation.outboundDepartureDate.toString().replace('T', ' '),
          return_departure_date: this.reservation.returnDepartureDate && this.reservation.returnDepartureDate.toString().replace('T', ' '),
          route_code: this.reservation.returnRouteCode ? `${this.reservation.outboundRouteCode} - ${this.reservation.returnRouteCode}` : this.reservation.outboundRouteCode,
          meals_included: this.reservation.mealsSelections && this.reservation.mealsSelections.out.selectedMeals?.length ? true : false,
          route_name: this.reservation.outboundRouteName,
          outbound_port_name: this.reservation.outboundRouteName,
          return_port_name: this.reservation.returnRouteName,
          outbound_route_code: this.reservation.outboundRouteCode,
          return_route_code: this.reservation.returnRouteCode,
          product_code: this.reservation.productCode,
          sales_owner: this.resourceService.getSalesOwner(),
          booking_type: this.reservation.productCode && this.reservation.productCode[0],
        },
      },
    }
    return purchaseEvent
  }

  private getPruchaseItems(): IPurchaseItem[] {
    let products = this.getProducts()
    let items: IPurchaseItem[] = products.map((product) => {
      let item: IPurchaseItem = {
        item_id: product.id,
        item_name: product.name,
        item_brand: product.brand,
        item_category: product.category,
        item_variant: product.variant,
        price: typeof product.price === 'string' ? parseFloat(product.price) : product.price ?? 0,
        quantity: product.quantity,
        leg: product.dimension1 ?? 'unknown',
      }
      return item
    })
    return items
  }

  private getProducts(): IProductTrack[] {
    return (
      this.tracking &&
      this.tracking.products.map(
        (p) =>
          <IProductTrack>{
            id: p.id,
            brand: 'SeaBook',
            name: p.name ? p.name : p.id,
            price: p.price.toFixed(2),
            variant: this.reservation.productCode[0],
            category: [this.reservation.outboundRouteCode, this.reservation.returnRouteCode].filter((c) => c).join('-'),
            quantity: p.quantity,
            dimension1: p.dimensions && p.dimensions.d1,
            dimension2: p.dimensions && p.dimensions.d2,
            dimension3: p.dimensions && p.dimensions.d3,
            dimension4: p.dimensions && p.dimensions.d4,
          }
      )
    )
  }

    private createEnhancedEcommerceTrack(): IEnhancedEcommerceTrack {
    let host = window.location.host.toLowerCase()
    let useUnifiedGTM = host.indexOf('.dfds.com') > 0 || host.indexOf('localhost') >= 0

    let enhancedEcommerceTrack = <IEnhancedEcommerceTrack>{
      isAgent: this.cookieService.isAgent(),
      event: 'checkout',
      businessArea: 'passenger',
      isReturnBooking: this.reservation.returnDepartureId ? 'Yes' : 'No',
      ecommerce: this.createEcommerce(),
      countryCode: this.localeService.getUnifiedCountryCode(),
      languageCode: this.localeService.getUnifiedLanguageCode(),
      market: this.marketService.getMarket(),
      bookingEngine: useUnifiedGTM ? 'seabookFlow' : 'seabook',

      // old tracking values, may be removed
      passengersAdults: '' + this.reservation.adults,
      passengersChild: '' + this.reservation.children,
      passengersInfant: '' + this.reservation.infants,
      // new tracking values
      numberAdults: '' + this.reservation.adults,
      numberChildren: '' + this.reservation.children,
      numberInfants: '' + this.reservation.infants,

      outboundPassengers: this.reservation.payingPassengers.toString(),
      returnPassengers: this.reservation.payingPassengers.toString(),
      outboundDepartureDate: this.reservation.outboundDepartureDate && this.reservation.outboundDepartureDate.toString().replace('T', ' '),
      returnDepartureDate: this.reservation.returnDepartureDate && this.reservation.returnDepartureDate.toString().replace('T', ' '),
      fullRouteCode: this.reservation.routeCode,
      mealsIncluded: this.reservation.mealsSelections && this.reservation.mealsSelections.out.selectedMeals.length ? 'Yes' : 'No',
      routeName: this.reservation.outboundRouteName,
      routeCode: this.reservation.routeCode,
      salesOwner: this.resourceService.getSalesOwner(),
      vehicleIncluded: this.reservation.vehicleAndTrailerTypes && this.reservation.vehicleAndTrailerTypes.length ? 'Yes' : 'No',
      vehicleType:
        this.reservation.vehicleAndTrailerTypes && this.reservation.vehicleAndTrailerTypes.length
          ? this.reservation.vehicleAndTrailerTypes[0]
          : '',
      arrivalPortName: this.reservation.outboundRouteName,
      departurePortName: this.reservation.returnRouteName,
      bookingType: this.reservation.productCode && this.reservation.productCode[0],
      outwardRouteCode: this.reservation.outboundRouteCode,
      returnRouteCode: this.reservation.returnRouteCode,
      productCode: this.reservation.productCode,
      userID: sessionStorage.getItem('pax_Sub'),
    }
    return enhancedEcommerceTrack
  }

  private createEcommerce(): IECommerceTrack {
    let ecommerceTrack = <IECommerceTrack>{}
     {
      ecommerceTrack.checkout = <ICheckoutTrack>{
        actionField: <IActionField>{
          step: this.step,
          option: this.reservation.outboundRouteCode && this.reservation.outboundRouteCode.match(/^DV|DV$/i) ? 'short' : 'long',
          revenue: this.tracking && this.tracking.price ? this.tracking.price.toFixed(2) : null,
        },
        products: this.getProducts(),
      }
    }
    return ecommerceTrack
  }
  
}


